export interface LocalCapture {
  stop(): Promise<void>;
  start(): Promise<void>;
  frames(handler: (samples: Float32Array) => void): void;
  setEnabled(enabled: boolean): void;
  flush(): Promise<void>;
}

export class BrowserLocalCapture implements LocalCapture {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private handler: ((samples: Float32Array) => void) | null = null;
  private generation = 0;
  private token = 0;
  private flushPending: {token:number;resolve:()=>void;reject:(error:Error)=>void;timer:ReturnType<typeof setTimeout>} | null = null;
  constructor(private readonly workletUrl: string) {}
  frames(handler: (samples: Float32Array) => void): void { this.handler = handler; }
  async start(): Promise<void> {
    if (this.context || this.stream) throw new Error('La captura ya está activa.');
    const generation = ++this.generation;
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({audio:{echoCancellation:true,noiseSuppression:true,channelCount:1},video:false});
      if (generation !== this.generation) { stream.getTracks().forEach(t=>t.stop()); return; }
      this.stream = stream;
      context = new AudioContext(); this.context = context;
      await context.resume();
      await context.audioWorklet.addModule(this.workletUrl);
      if (generation !== this.generation) { stream.getTracks().forEach(t=>t.stop()); if(context.state!=='closed') await context.close(); return; }
      this.source = context.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(context,'asis-local-capture',{numberOfInputs:1,numberOfOutputs:0,channelCount:1});
      this.node = node;
      node.port.onmessage = (event:MessageEvent<{type:string;samples?:Float32Array;token?:number}>)=>{
        if(generation!==this.generation) return;
        if(event.data.type==='frame'&&event.data.samples) this.handler?.(event.data.samples);
        if(event.data.type==='flushed'&&this.flushPending&&this.flushPending.token===event.data.token){const p=this.flushPending;this.flushPending=null;clearTimeout(p.timer);p.resolve();}
      };
      node.port.start?.();
      node.port.postMessage({type:'enabled',value:false});
      this.source.connect(node);
    } catch(error) {
      if(generation===this.generation) await this.stop();
      else {stream?.getTracks().forEach(t=>t.stop());if(context&&context.state!=='closed') await context.close();}
      throw error;
    }
  }
  setEnabled(enabled:boolean):void { this.node?.port.postMessage({type:'enabled',value:enabled}); }
  flush():Promise<void> {
    if(!this.node) return Promise.reject(new Error('Captura no activa.'));
    if(this.flushPending) return Promise.reject(new Error('Captura terminando.'));
    const token=++this.token;
    return new Promise((resolve,reject)=>{
      const timer=setTimeout(()=>{if(this.flushPending?.token!==token)return;this.flushPending=null;this.setEnabled(false);reject(new Error('La captura no confirmó el final; muestra descartada.'));},500);
      this.flushPending={token,resolve,reject,timer};
      this.node!.port.postMessage({type:'flush',token});
    });
  }
  async stop():Promise<void> {
    ++this.generation;
    const pending=this.flushPending;this.flushPending=null;
    if(pending){clearTimeout(pending.timer);pending.reject(new Error('Captura cancelada.'));}
    if(this.node){this.node.port.onmessage=null;this.node.port.close();this.node.disconnect();}this.node=null;
    this.source?.disconnect();this.source=null;
    this.stream?.getTracks().forEach(t=>t.stop());this.stream=null;
    const context=this.context;this.context=null;
    if(context&&context.state!=='closed') await context.close();
  }
}
